import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, AlertCircle, CheckCircle2, Send } from 'lucide-react';
import ohipInvoicesService from '../services/OhipInvoicesService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import './InvoiceWizard.css';

// Bulk invoicing — choose environments, see what each would be billed, then generate.
//
// THREE STEPS, AND THE MIDDLE ONE IS THE POINT. Anyone can press "invoice everything"; what makes that
// safe is seeing the amounts, the request counts and the environments that CANNOT be billed before
// committing. The preview is computed by the same server code that generation writes, so the figures on
// step 2 are the figures that land.
//
// Used both for "Invoice all" (opens with everything selected) and for one environment from the OHIP
// Transactions screen (opens with just that one, pre-selected).

const STEPS = ['Choose environments', 'Review', 'Generate'];

const money = (n, ccy) => (n == null ? '—'
    : `${ccy || 'GBP'} ${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-GB'));

// Default to LAST month: an invoice is raised for a period that has finished, and defaulting to the
// current one invites a bill for a month that is still accruing.
const lastMonth = () => {
    const d = new Date();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() - 1);
    return d.toISOString().slice(0, 7);
};

function InvoiceWizard({ initialEnvironmentIds = null, initialHotelCodes = null, initialMonth = null, onClose, onDone }) {
    const [step, setStep] = useState(0);
    const [month, setMonth] = useState(initialMonth || lastMonth());
    const [environments, setEnvironments] = useState([]);
    const [selected, setSelected] = useState(() => new Set(initialEnvironmentIds || []));
    const [search, setSearch] = useState('');
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [sendToClearbooks, setSendToClearbooks] = useState(false);
    const [clearbooksReady, setClearbooksReady] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([ohipInvoicesService.environments(), ohipInvoicesService.config()])
            .then(([envRes, cfgRes]) => {
                const list = envRes.data || [];
                setEnvironments(list);
                setClearbooksReady(!!(cfgRes.data && cfgRes.data.clearbooksConfigured));
                if (initialEnvironmentIds) return;                       // caller named them outright
                if (initialHotelCodes && initialHotelCodes.length) {
                    // PRESELECTED BY HOTEL CODE, not by name. The OHIP Transactions screen filters on the
                    // portal's environment GUID, which the invoiceable rows do not hold — the hotel codes
                    // are the id the two sides share. An environment is selected if it contains any of
                    // them, because that is what "invoice what I am looking at" means.
                    const want = new Set(initialHotelCodes);
                    const hit = list.filter(e => String(e.codes || '').split(',').some(c => want.has(c.trim())));
                    setSelected(new Set(hit.map(e => e.id)));
                    return;
                }
                setSelected(new Set(list.map(e => e.id)));               // "Invoice all"
            })
            .catch(e => setError(e.response?.data?.message || e.message || 'Failed to load environments'))
            .finally(() => setLoading(false));
    }, [initialEnvironmentIds, initialHotelCodes]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return environments;
        return environments.filter(e =>
            (e.environmentName || '').toLowerCase().includes(q)
            || (e.environmentKey || '').toLowerCase().includes(q)
            || (e.name || '').toLowerCase().includes(q));
    }, [environments, search]);

    const toggle = (id) => setSelected((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const runPreview = useCallback(async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await ohipInvoicesService.preview({ month, environmentIds: [...selected] });
            setPreview(res.data);
            setStep(1);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not price these environments');
        } finally {
            setBusy(false);
        }
    }, [month, selected]);

    const runGenerate = async () => {
        setBusy(true);
        setError(null);
        try {
            // Only what the preview says is actually billable, and only what has usage — generating an
            // invoice for zero is noise in the customer's accounts, and the server skips it anyway.
            const ids = (preview.invoices || []).filter(i => i.totalRequests > 0).map(i => i.environmentId);
            const res = await ohipInvoicesService.generate({ month, environmentIds: ids, send: sendToClearbooks });
            setResult(res.data);
            setStep(2);
            if (onDone) onDone();
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not generate the invoices');
        } finally {
            setBusy(false);
        }
    };

    const withUsage = (preview?.invoices || []).filter(i => i.totalRequests > 0);
    const unbillable = withUsage.filter(i => !i.billable);

    return (
        <div className="iw-backdrop" onClick={onClose}>
            <div className="iw-modal" onClick={e => e.stopPropagation()}>
                <div className="iw-header">
                    <div>
                        <h2>Generate invoices</h2>
                        <div className="iw-steps">
                            {STEPS.map((s, i) => (
                                <span key={s} className={`iw-step ${i === step ? 'iw-step-on' : ''} ${i < step ? 'iw-step-done' : ''}`}>
                                    {i + 1}. {s}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button className="iw-close" onClick={onClose} title="Close"><X size={18} /></button>
                </div>

                {error && <div className="iw-error"><AlertCircle size={16} /> {error}</div>}

                <div className="iw-body">
                    {loading && <Spinner />}

                    {!loading && step === 0 && (
                        <>
                            <div className="iw-controls">
                                <label>
                                    <span>Month</span>
                                    <input type="month" value={month} onChange={e => setMonth(e.target.value)} />
                                </label>
                                <label className="iw-grow">
                                    <span>Filter</span>
                                    <input type="text" value={search} placeholder="Environment name, key or hotel code"
                                           onChange={e => setSearch(e.target.value)} />
                                </label>
                                <div className="iw-bulk">
                                    <button onClick={() => setSelected(new Set(visible.map(e => e.id)))}>Select all</button>
                                    <button onClick={() => setSelected(new Set())}>Clear</button>
                                </div>
                            </div>
                            <div className="iw-list">
                                {visible.map(e => (
                                    <label key={e.id} className="iw-row">
                                        <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
                                        <span className="iw-row-name">{e.environmentName || e.name || e.environmentKey}</span>
                                        <code className="iw-row-key">{e.environmentKey}</code>
                                        {e.dormant === 'Y' && <span className="iw-chip iw-chip-warn">dormant</span>}
                                        {!e.partnerId && !e.accountId && !e.hotelId && (
                                            <span className="iw-chip">unattributed</span>
                                        )}
                                    </label>
                                ))}
                                {!visible.length && <div className="iw-empty">No environments match.</div>}
                            </div>
                        </>
                    )}

                    {!loading && step === 1 && preview && (
                        <>
                            <div className="iw-summary">
                                <div><span>Period</span>{preview.period.start} → {preview.period.end}</div>
                                <div><span>With usage</span>{num(preview.totals.withUsage)} of {num(preview.totals.environments)}</div>
                                <div><span>Requests</span>{num(preview.totals.requests)}</div>
                                <div><span>Total</span><strong>{money(preview.totals.amount)}</strong></div>
                            </div>

                            {/* Named, not counted. "3 cannot be billed" sends the reader hunting; the list
                                tells them which and why, which is the whole of the fix. */}
                            {unbillable.length > 0 && (
                                <div className="iw-warn">
                                    <AlertCircle size={15} />
                                    <div>
                                        <strong>{unbillable.length} cannot be sent to Clear Books</strong> — they will still be
                                        generated as drafts here.
                                        <ul>
                                            {unbillable.slice(0, 6).map(i => (
                                                <li key={i.environmentId}>{i.environmentName} — {i.unbillableReason}</li>
                                            ))}
                                            {unbillable.length > 6 && <li>…and {unbillable.length - 6} more</li>}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            <table className="iw-table">
                                <thead>
                                    <tr>
                                        <th>Environment</th>
                                        <th className="iw-num">Requests</th>
                                        <th className="iw-num">Billable</th>
                                        <th className="iw-num">Rate /1k</th>
                                        <th>Scope</th>
                                        <th className="iw-num">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {withUsage.map(i => (
                                        <tr key={i.environmentId} className={i.billable ? '' : 'iw-row-warn'}>
                                            <td>
                                                {i.environmentName}
                                                <code className="iw-row-key">{i.environmentKey}</code>
                                            </td>
                                            <td className="iw-num">{num(i.totalRequests)}</td>
                                            <td className="iw-num">{num(i.billableRequests)}</td>
                                            <td className="iw-num">{Number(i.ratePer1000).toFixed(4)}</td>
                                            <td><span className="iw-chip">{i.rateScope}</span></td>
                                            <td className="iw-num">{money(i.amount, i.currency)}{i.minimumApplied && <span className="iw-chip iw-chip-warn">min</span>}</td>
                                        </tr>
                                    ))}
                                    {!withUsage.length && (
                                        <tr><td colSpan={6} className="iw-empty">No usage in {month} for the selected environments.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}

                    {!loading && step === 2 && result && (
                        <div className="iw-result">
                            <div className="iw-result-head">
                                <CheckCircle2 size={18} />
                                <strong>{num(result.generated)} invoice(s) generated for {result.month}</strong>
                            </div>
                            <ul>
                                {result.skipped > 0 && <li>{num(result.skipped)} skipped (no usage, or already sent)</li>}
                                {result.sent > 0 && <li>{num(result.sent)} sent to Clear Books</li>}
                                {result.failedToSend > 0 && <li className="iw-bad">{num(result.failedToSend)} could not be sent — the reason is on each invoice</li>}
                            </ul>
                            <div className="iw-result-list">
                                {(result.results || []).filter(r => r.skipped || r.sendError).map((r, n) => (
                                    <div key={n} className="iw-result-row">
                                        <code>{r.environmentKey}</code> {r.reason || r.sendError}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="iw-footer">
                    {step === 1 && (
                        <label className="iw-send" title={clearbooksReady ? '' : 'Clear Books is not configured in this environment'}>
                            <input type="checkbox" checked={sendToClearbooks} disabled={!clearbooksReady}
                                   onChange={e => setSendToClearbooks(e.target.checked)} />
                            <Send size={14} />
                            <span>{clearbooksReady ? 'Also send to Clear Books' : 'Clear Books not configured'}</span>
                        </label>
                    )}
                    <div className="iw-footer-actions">
                        {step === 1 && <Button variant="secondary" onClick={() => setStep(0)} disabled={busy}><ChevronLeft className="icon" /> Back</Button>}
                        {step === 0 && (
                            <Button variant="success" onClick={runPreview} disabled={busy || !selected.size}>
                                {busy ? 'Pricing…' : <>Review {selected.size} <ChevronRight className="icon" /></>}
                            </Button>
                        )}
                        {step === 1 && (
                            <Button variant="success" onClick={runGenerate} disabled={busy || !withUsage.length}>
                                {busy ? 'Generating…' : `Generate ${withUsage.length}`}
                            </Button>
                        )}
                        {step === 2 && <Button variant="success" onClick={onClose}>Done</Button>}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InvoiceWizard;
